import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  TrendingUp, 
  Target, 
  Brain, 
  PiggyBank, 
  Shield, 
  BarChart3,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  ChevronDown,
  HelpCircle
} from 'lucide-react';

// Hook to detect mobile
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
}

// Animated counter component
function AnimatedCounter({ value, suffix = '', prefix = '', duration = 2 }) {
  const [count, setCount] = useState(0);
  const hasAnimated = useRef(false);
  const ref = useRef(null);
  
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    
    const startCounting = () => {
      if (hasAnimated.current) return;
      hasAnimated.current = true;
      
      let start = 0;
      const end = parseFloat(value);
      const isDecimal = value.toString().includes('.');
      const steps = isDecimal ? end * 10 : end;
      const incrementTime = (duration * 1000) / steps;
      
      const timer = setInterval(() => {
        start += isDecimal ? 0.1 : 1;
        setCount(isDecimal ? parseFloat(start.toFixed(1)) : start);
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        }
      }, incrementTime);
    };
    
    // Use native IntersectionObserver for reliability
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) startCounting(); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    
    // Safety fallback: if observer never fires (e.g. parent is opacity:0), start after 3s
    const fallback = setTimeout(() => { if (!hasAnimated.current) startCounting(); }, 3000);
    
    return () => { observer.disconnect(); clearTimeout(fallback); };
  }, [value, duration]);
  
  return <span ref={ref}>{prefix}{count}{suffix}</span>;
}

// Mobile ambient glow - lightweight CSS-only background effects
function MobileAmbientGlow({ color = 'teal' }) {
  const colors = color === 'teal' 
    ? 'from-teal-500/15 to-emerald-500/10'
    : 'from-purple-500/10 to-pink-500/10';
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none md:hidden">
      <div className={`absolute w-72 h-72 rounded-full bg-gradient-to-r ${colors} blur-3xl -top-20 -left-20 animate-pulse`} style={{ animationDuration: '4s' }} />
      <div className={`absolute w-56 h-56 rounded-full bg-gradient-to-r from-cyan-500/10 to-teal-500/8 blur-3xl -bottom-16 -right-16 animate-pulse`} style={{ animationDuration: '5s', animationDelay: '2s' }} />
    </div>
  );
}

// Floating gradient orbs for background - Full version on desktop, light CSS version on mobile
function FloatingOrbs() {
  return (
    <>
      {/* Desktop: JS-animated orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
        <motion.div
          className="absolute w-96 h-96 rounded-full bg-gradient-to-r from-teal-500/20 to-emerald-500/20 blur-3xl"
          animate={{ x: [0, 80, 0], y: [0, -40, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          style={{ top: '5%', left: '5%' }}
        />
        <motion.div
          className="absolute w-80 h-80 rounded-full bg-gradient-to-r from-purple-500/15 to-pink-500/15 blur-3xl"
          animate={{ x: [0, -60, 0], y: [0, 50, 0], scale: [1, 1.2, 1] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          style={{ top: '20%', right: '10%' }}
        />
        <motion.div
          className="absolute w-64 h-64 rounded-full bg-gradient-to-r from-cyan-500/10 to-blue-500/10 blur-3xl"
          animate={{ x: [0, 40, 0], y: [0, 60, 0], scale: [1.1, 1, 1.1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          style={{ bottom: '15%', left: '15%' }}
        />
      </div>
      {/* Mobile: CSS-only ambient glow */}
      <MobileAmbientGlow color="teal" />
    </>
  );
}

// Scroll-triggered section wrapper - Polished on mobile
function AnimatedSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-30px" });
  const isMobile = useIsMobile();
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: isMobile ? 30 : 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: isMobile ? 30 : 50 }}
      transition={{ duration: isMobile ? 0.5 : 0.6, delay: isMobile ? 0.05 : delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// Staggered children animation - Visible stagger on mobile
function StaggerContainer({ children, className = '', staggerDelay = 0.1 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-30px" });
  const isMobile = useIsMobile();
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={{
        visible: {
          transition: {
            staggerChildren: isMobile ? 0.08 : staggerDelay
          }
        }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const staggerChild = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.4, ease: "easeOut" }
  }
};

export default function LandingPage() {
  const [openFAQ, setOpenFAQ] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0.9]);
  const isMobile = useIsMobile();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: "Is FinAI really free?",
      answer: "Yes! FinAI is free to start with all core features included. Track your transactions, create budgets, set goals, manage investments, and get AI-powered insights - all at no cost. We may introduce premium features in the future, but the core functionality will always remain free."
    },
    {
      question: "Is my financial data secure?",
      answer: "Absolutely. We use bank-level encryption to protect your data. Your financial information is stored securely and privately. We never share your data with third parties, and you have full control over what information you enter. Best of all, we don't require linking your bank account - you manually enter your data."
    },
    {
      question: "How does the AI work?",
      answer: "Our AI is powered by GPT-4o-mini, the same advanced technology behind ChatGPT. It analyzes your income, expenses, investments, and financial goals to generate personalized recommendations. The AI learns from your financial patterns and provides actionable insights to help you save more, invest smarter, and achieve your goals faster."
    },
    {
      question: "Do I need to link my bank account?",
      answer: "No! FinAI doesn't require bank account linking. You have complete control - simply enter your transactions, income, and expenses manually. This gives you privacy and security while still getting powerful AI-powered insights. You decide what information to share."
    },
    {
      question: "What features are included?",
      answer: "FinAI includes everything you need for comprehensive financial planning: Transaction tracking (income & expenses), Budget planning and monitoring, Goal setting and tracking, Investment portfolio management (stocks, mutual funds, PPF, EPF, gold), Bills & reminders, Loan/debt tracking, AI-powered financial advisor, Detailed reports and analytics, Retirement calculator and financial planners."
    },
    {
      question: "Can I use FinAI on my mobile phone?",
      answer: "Yes! FinAI is fully responsive and works seamlessly on mobile phones, tablets, and desktop computers. Whether you're on iOS or Android, you can access your financial data anytime, anywhere through your mobile browser. The interface adapts beautifully to any screen size."
    }
  ];

  const features = [
    { icon: Brain, color: "from-teal-500 to-emerald-600", title: "AI Financial Planning", desc: "Get personalized financial plans with AI-powered recommendations. Know exactly how much to save, invest, and spend." },
    { icon: TrendingUp, color: "from-purple-500 to-pink-600", title: "Investment Tracking", desc: "Track stocks, mutual funds, gold, PPF, EPF, and FDs all in one place. See your complete portfolio performance." },
    { icon: Target, color: "from-orange-500 to-red-600", title: "Goal-Based Planning", desc: "Set financial goals (house, education, retirement) and get a clear roadmap with monthly investment targets." },
    { icon: PiggyBank, color: "from-blue-500 to-cyan-600", title: "Expense Management", desc: "Track daily expenses, set budgets, and see where your money goes. Reduce unnecessary spending effortlessly." },
    { icon: Shield, color: "from-green-500 to-teal-600", title: "Debt & EMI Tracking", desc: "Manage loans and EMIs smartly. Get alerts for due dates and optimization suggestions to save on interest." },
    { icon: BarChart3, color: "from-yellow-500 to-orange-600", title: "Net Worth Tracking", desc: "See your complete financial picture. Track assets minus liabilities and watch your net worth grow over time." }
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white overflow-x-hidden">
      {/* Header/Navigation */}
      <motion.header 
        className="fixed top-0 w-full bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 z-50"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <motion.div 
                className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center"
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <span className="text-white font-bold text-lg">FA</span>
              </motion.div>
              <span className="text-xl font-bold">FinAI</span>
            </Link>

            {/* Navigation Links */}
            <nav className="hidden md:flex items-center space-x-6">
              <button 
                onClick={() => scrollToSection('features')}
                className="text-gray-300 hover:text-white transition-colors"
              >
                Features
              </button>
              <button 
                onClick={() => scrollToSection('how-it-works')}
                className="text-gray-300 hover:text-white transition-colors"
              >
                How It Works
              </button>
              <Link 
                to="/advisors" 
                className="text-gray-300 hover:text-white transition-colors"
              >
                For Advisors
              </Link>
              <Link 
                to="/login" 
                className="text-gray-300 hover:text-white transition-colors"
              >
                Login
              </Link>
              <Link to="/register">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700">
                    Start Free
                  </Button>
                </motion.div>
              </Link>
            </nav>

            {/* Mobile Menu Buttons */}
            <div className="md:hidden flex items-center space-x-2">
              <Link to="/advisors">
                <Button variant="ghost" size="sm" className="text-gray-300 text-xs">
                  For Advisors
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-white text-xs">
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-gradient-to-r from-teal-500 to-emerald-600 text-xs">
                  Sign Up
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative" ref={heroRef}>
        <FloatingOrbs />
        <motion.div style={{ opacity: isMobile ? 1 : heroOpacity }} className="container mx-auto max-w-6xl relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Column - Text Content */}
            <div className="space-y-6 text-center md:text-left">
              {/* Badge */}
              <motion.div 
                className="inline-flex items-center space-x-2 bg-gray-800 px-4 py-2 rounded-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Brain className="w-4 h-4 text-teal-500" />
                <span className="text-sm text-gray-300">AI-Powered Financial Planning</span>
              </motion.div>

              {/* Headline */}
              <motion.h1 
                className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: isMobile ? 0 : 0.2 }}
              >
                India's AI Financial Planning App
                <br />
                <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent inline-block">
                  for Salaried Professionals
                </span>
              </motion.h1>

              {/* Subheadline */}
              <motion.p 
                className="text-xl text-gray-400"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: isMobile ? 0.1 : 0.4 }}
              >
                Get personalized AI-powered financial plans in 5 minutes. 
                Built for Indians, by Indians.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div 
                className="flex flex-col sm:flex-row items-center md:items-start gap-4 pt-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: isMobile ? 0.2 : 0.6 }}
              >
                <Link to="/register">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button 
                      size="lg" 
                      className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-lg px-8 py-6 shadow-lg shadow-teal-500/25"
                    >
                      Get My Free Financial Plan <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </motion.div>
                </Link>
                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-gray-700 text-white hover:bg-gray-800 py-6"
                    onClick={() => scrollToSection('how-it-works')}
                  >
                    See How It Works
                  </Button>
                </motion.div>
              </motion.div>

              {/* Micro-copy under CTA */}
              <motion.p 
                className="text-sm text-gray-500"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: isMobile ? 0.3 : 0.8 }}
              >
                No credit card • Takes 5 minutes • Free to start
              </motion.p>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 pt-4 text-sm text-gray-400">
                {[
                  { text: "Works on mobile" },
                  { text: "Bank-level security" },
                  { text: "AI-powered" }
                ].map((item, i) => (
                  <motion.div 
                    key={i}
                    className="flex items-center space-x-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: isMobile ? 0.3 : (1 + i * 0.1) }}
                  >
                    <CheckCircle2 className="w-5 h-5 text-teal-500" />
                    <span>{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Right Column - App Screenshot/Demo */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: isMobile ? 0.2 : 0.3 }}
            >
              <motion.div 
                className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-700"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                {/* Mock Dashboard Screenshot */}
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-center justify-between pb-4 border-b border-gray-700">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center">
                        <span className="text-white font-bold">FA</span>
                      </div>
                      <div>
                        <p className="text-white font-semibold">FinAI Dashboard</p>
                        <p className="text-gray-400 text-xs">Real-time insights</p>
                      </div>
                    </div>
                  </div>

                  {/* AI Chat Preview */}
                  <div className="bg-gray-900 rounded-xl p-4 space-y-3">
                    <div className="flex items-start space-x-2">
                      <Sparkles className="w-4 h-4 text-purple-400 mt-1" />
                      <div>
                        <p className="text-xs text-purple-400 font-semibold mb-1">AI Advisor</p>
                        <p className="text-sm text-gray-300">Based on your spending pattern, I recommend saving ₹15,000 monthly to reach your goal...</p>
                      </div>
                    </div>
                  </div>

                  {/* Stats Cards */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gradient-to-br from-teal-600 to-emerald-600 rounded-lg p-3">
                      <p className="text-xs text-teal-100">Total Savings</p>
                      <p className="text-xl font-bold text-white">
                        <AnimatedCounter prefix="₹" value="2.5" suffix="L" duration={1.5} />
                      </p>
                      <p className="text-xs text-teal-100">↑ 12% this month</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg p-3">
                      <p className="text-xs text-purple-100">Investments</p>
                      <p className="text-xl font-bold text-white">
                        <AnimatedCounter prefix="₹" value="8.2" suffix="L" duration={1.5} />
                      </p>
                      <p className="text-xs text-purple-100">↑ 5.2% returns</p>
                    </div>
                  </div>

                  {/* AI Badge */}
                  <div className="flex items-center justify-center space-x-2 bg-purple-900/30 rounded-lg py-2 border border-purple-700">
                    <Brain className="w-4 h-4 text-purple-400" />
                    <span className="text-xs text-purple-300">Powered by AI</span>
                  </div>
                </div>

                {/* Animated glow effects */}
                {!isMobile ? (
                  <>
                    <motion.div 
                      className="absolute -top-4 -right-4 w-24 h-24 bg-teal-500/20 rounded-full blur-3xl"
                      animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
                      transition={{ duration: 3, repeat: Infinity }}
                    />
                    <motion.div 
                      className="absolute -bottom-4 -left-4 w-24 h-24 bg-purple-500/20 rounded-full blur-3xl"
                      animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.2, 0.3] }}
                      transition={{ duration: 4, repeat: Infinity }}
                    />
                  </>
                ) : (
                  <>
                    <div className="absolute -top-3 -right-3 w-20 h-20 bg-teal-500/20 rounded-full blur-2xl animate-pulse" style={{ animationDuration: '3s' }} />
                    <div className="absolute -bottom-3 -left-3 w-20 h-20 bg-purple-500/15 rounded-full blur-2xl animate-pulse" style={{ animationDuration: '4s', animationDelay: '1s' }} />
                  </>
                )}
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 md:py-20 px-4 bg-gray-800/50 relative">
        <MobileAmbientGlow color="teal" />
        <div className="container mx-auto max-w-6xl relative z-10">
          <AnimatedSection className="text-center mb-10 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Everything You Need to Manage Your Finances
            </h2>
            <p className="text-gray-400 text-base md:text-lg">
              All-in-one platform for tracking, planning, and growing your wealth
            </p>
            <div className="w-16 h-1 bg-gradient-to-r from-teal-500 to-emerald-500 mx-auto mt-4 rounded-full md:hidden" />
          </AnimatedSection>

          <StaggerContainer className="grid md:grid-cols-3 gap-4 md:gap-6" staggerDelay={0.1}>
            {features.map((feature, i) => (
              <motion.div key={i} variants={staggerChild}>
                <motion.div
                  whileHover={{ y: -10, scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Card className="bg-gray-800/60 md:bg-gray-800 backdrop-blur-sm md:backdrop-blur-none border-white/5 md:border-gray-700 p-5 md:p-6 hover:border-teal-500/50 transition-all duration-300 h-full">
                    <motion.div 
                      className={`w-11 h-11 md:w-12 md:h-12 bg-gradient-to-br ${feature.color} rounded-lg flex items-center justify-center mb-3 md:mb-4 shadow-lg`}
                      whileHover={{ rotate: 10, scale: 1.1 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <feature.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </motion.div>
                    <h3 className="text-lg md:text-xl font-semibold mb-2 text-white">{feature.title}</h3>
                    <p className="text-sm md:text-base text-gray-400">{feature.desc}</p>
                  </Card>
                </motion.div>
              </motion.div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 md:py-20 px-4 relative">
        <div className="container mx-auto max-w-4xl relative z-10">
          <AnimatedSection className="text-center mb-10 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Get Started in 3 Simple Steps
            </h2>
            <p className="text-gray-400 text-base md:text-lg">
              From signup to your first financial plan in under 5 minutes
            </p>
            <div className="w-16 h-1 bg-gradient-to-r from-teal-500 to-emerald-500 mx-auto mt-4 rounded-full md:hidden" />
          </AnimatedSection>

          <StaggerContainer className="space-y-6 md:space-y-8" staggerDelay={0.2}>
            {[
              { num: "1", title: "Sign Up Free", desc: "Create your account in 30 seconds. No credit card required. Just your email and password." },
              { num: "2", title: "Add Your Financial Details", desc: "Enter your income, expenses, investments, and goals. Takes 3-4 minutes to complete." },
              { num: "3", title: "Get Your AI Financial Plan", desc: "Our AI analyzes your situation and generates a personalized plan with actionable recommendations." }
            ].map((step, i) => (
              <motion.div 
                key={i} 
                variants={staggerChild}
                className="flex items-start space-x-4 bg-gray-800/40 md:bg-transparent backdrop-blur-sm md:backdrop-blur-none rounded-xl p-4 md:p-0 border border-white/5 md:border-0"
              >
                <motion.div 
                  className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center text-xl font-bold"
                  whileHover={{ scale: 1.1, rotate: 10 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {step.num}
                </motion.div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-gray-400">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-20 px-4 bg-gray-800 relative">
        <div className="container mx-auto max-w-4xl relative z-10">
          {/* Section Header */}
          <AnimatedSection className="text-center mb-10 md:mb-12">
            <motion.div 
              className="inline-flex items-center space-x-2 bg-gray-700/60 backdrop-blur-sm px-4 py-2 rounded-full mb-4 border border-white/5"
              whileHover={{ scale: 1.05 }}
            >
              <HelpCircle className="w-4 h-4 text-teal-500" />
              <span className="text-sm text-gray-300">Got Questions?</span>
            </motion.div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-400">
              Get answers to common questions about FinAI
            </p>
            <div className="w-16 h-1 bg-gradient-to-r from-teal-500 to-emerald-500 mx-auto mt-4 rounded-full md:hidden" />
          </AnimatedSection>

          {/* FAQ Accordion */}
          <StaggerContainer className="space-y-3 md:space-y-4" staggerDelay={0.08}>
            {faqs.map((faq, index) => (
              <motion.div 
                key={index}
                variants={staggerChild}
                className="bg-gray-900/70 md:bg-gray-900 backdrop-blur-sm md:backdrop-blur-none rounded-lg border border-white/5 md:border-gray-700 overflow-hidden transition-all hover:border-gray-600"
              >
                <motion.button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-800 transition-colors"
                  whileHover={{ x: 5 }}
                >
                  <span className="text-lg font-semibold text-gray-100 pr-4">
                    {faq.question}
                  </span>
                  <motion.div
                    animate={{ rotate: openFAQ === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown className="w-5 h-5 text-teal-500 flex-shrink-0" />
                  </motion.div>
                </motion.button>
                
                <motion.div
                  initial={false}
                  animate={{ 
                    height: openFAQ === index ? "auto" : 0,
                    opacity: openFAQ === index ? 1 : 0
                  }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-5 pt-2">
                    <p className="text-gray-400 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </StaggerContainer>

          {/* Still have questions CTA */}
          <AnimatedSection delay={0.3} className="text-center mt-12">
            <p className="text-gray-400 mb-4">Still have questions?</p>
            <Link to="/register">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button 
                  variant="outline"
                  className="border-gray-700 text-white hover:bg-gray-800"
                >
                  Get Started & Explore
                </Button>
              </motion.div>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-20 px-4 bg-gradient-to-r from-teal-600 to-emerald-700 relative overflow-hidden">
        {/* Animated background particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(isMobile ? 3 : 5)].map((_, i) => (
            <motion.div
              key={i}
              className={`absolute ${isMobile ? 'w-20 h-20' : 'w-32 h-32'} bg-white/5 rounded-full`}
              animate={{
                x: [0, isMobile ? 50 : 100, 0],
                y: [0, isMobile ? -25 : -50, 0],
                scale: [1, 1.2, 1],
                opacity: [0.05, 0.15, 0.05]
              }}
              transition={{
                duration: 10 + i * 2,
                repeat: Infinity,
                delay: i * 2
              }}
              style={{
                left: `${20 * i}%`,
                top: `${30 + i * 10}%`
              }}
            />
          ))}
        </div>

        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <motion.h2 
            className="text-3xl md:text-4xl font-bold mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Ready to Take Control of Your Finances?
          </motion.h2>
          <motion.p 
            className="text-lg text-teal-100 mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Join early adopters building better financial futures with FinAI
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Link to="/register">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button 
                  size="lg" 
                  className="bg-white text-teal-600 hover:bg-gray-100 text-lg px-8 py-6 shadow-xl"
                >
                  Get My Free Financial Plan <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </motion.div>
            </Link>
          </motion.div>
          <motion.p 
            className="text-sm text-teal-100 mt-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            No credit card • Takes 5 minutes • Free to start
          </motion.p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 py-8 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Brand Section */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <motion.div 
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                >
                  <span className="text-white font-bold text-lg">FA</span>
                </motion.div>
                <div>
                  <h3 className="text-lg font-bold text-white">FinAI</h3>
                  <p className="text-xs text-gray-400">Your Smart Money Manager</p>
                </div>
              </div>
              <p className="text-sm text-gray-400 max-w-md mb-4">
                Take control of your finances with AI-powered insights, comprehensive tracking, and personalized financial planning.
              </p>
              <Link to="/advisors" className="text-sm text-orange-400 hover:text-orange-300 transition-colors">
                <BarChart3 className="inline w-4 h-4 mr-1" />
                Are you a financial advisor? See our professional platform<span className="whitespace-nowrap"> →</span>
              </Link>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <button 
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="text-sm text-gray-400 hover:text-teal-400 transition-colors text-left"
                  >
                    About Us
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('features')}
                    className="text-sm text-gray-400 hover:text-teal-400 transition-colors text-left"
                  >
                    Features
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('how-it-works')}
                    className="text-sm text-gray-400 hover:text-teal-400 transition-colors text-left"
                  >
                    Pricing
                  </button>
                </li>
                <li>
                  <a href="mailto:support@finaigpt.com" className="text-sm text-gray-400 hover:text-teal-400 transition-colors">
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/privacy" className="text-sm text-gray-400 hover:text-teal-400 transition-colors flex items-center">
                    <Shield className="w-3 h-3 mr-2" />
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-sm text-gray-400 hover:text-teal-400 transition-colors flex items-center">
                    <CheckCircle2 className="w-3 h-3 mr-2" />
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <a href="mailto:support@finaigpt.com" className="text-sm text-gray-400 hover:text-teal-400 transition-colors flex items-center">
                    <Brain className="w-3 h-3 mr-2" />
                    Support
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-6 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400">
              © {new Date().getFullYear()} FinAI. All rights reserved.
            </p>
            <motion.div 
              className="flex items-center gap-2 text-sm text-gray-400"
              whileHover={{ scale: 1.05 }}
            >
              <span>Made with</span>
              <motion.span 
                className="text-red-500"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                ❤️
              </motion.span>
              <span>in India</span>
              <span className="text-lg">🇮🇳</span>
            </motion.div>
          </div>
        </div>
      </footer>
    </div>
  );
}
